import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, ClipboardCheck, Copy, Download, ExternalLink, RefreshCw, Send, Video, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { errMsg } from '../../lib/aiInterviewApi';
import { absUrl, browserTz, fmtSlot, isPast, schedulingAPI, SCHED_STATUS } from '../../lib/schedulingApi';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { copyText, downloadBlob, fmtDate, Pill } from '../aiinterview/recruiter/common';
import DirectSlots from './DirectSlots';
import SyncBadge, { syncMeta } from './SyncBadge';

const KV = ({ k, children }) => <div className="iv-hist iv-small"><span>{k}</span><b>{children}</b></div>;

export default function InterviewDetailDialog({ booking, onClose, onChanged }) {
  const [b, setB] = useState(booking);
  const [mode, setMode] = useState(null);
  const [slot, setSlot] = useState(null);
  const [who, setWho] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setB(booking); setMode(null); setNote(''); }, [booking]);
  if (!b) return null;
  const tz = browserTz();
  const meta = SCHED_STATUS[b.status] || { label: b.status, tone: 'slate' };
  const open = ['awaiting_candidate', 'scheduled'].includes(b.status);
  const past = b.status === 'scheduled' && isPast(b.end);
  const started = b.status === 'scheduled' && isPast(b.start);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const { data } = await fn();
      if (data?.id) setB(data);
      toast.success(ok);
      setMode(null);
      setNote('');
      onChanged();
    } catch (e) {
      toast.error(errMsg(e, 'Action failed'));
    } finally {
      setBusy(false);
    }
  };
  const retrySync = async () => {
    setBusy(true);
    try {
      const { data } = await schedulingAPI.syncCalendar(b.id);
      setB(data);
      onChanged();
      if (data.calendar_sync?.status === 'synced') toast.success(`${syncMeta(data.calendar_sync).label}`);
      else toast.error(data.calendar_sync?.error || 'Sync failed again');
    } catch (e) {
      toast.error(errMsg(e, 'Could not sync the calendar'));
    } finally {
      setBusy(false);
    }
  };
  const ics = async () => {
    try { downloadBlob((await schedulingAPI.ics(b.id)).data, 'interview.ics'); } catch (e) { toast.error('Could not download the invite'); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="iv sc-dialog max-w-2xl" style={{ maxHeight: '92vh', overflowY: 'auto' }} data-testid="interview-detail-dialog">
        <DialogHeader>
          <DialogTitle>{b.title}: {b.candidate_name}</DialogTitle>
          <DialogDescription>{b.job_title} · pool {b.pool_name}</DialogDescription>
        </DialogHeader>
        <div className="iv-row"><Pill tone={meta.tone} testId="detail-status">{meta.label}</Pill><SyncBadge b={b} testId="detail-sync-badge" /></div>
        {b.status === 'scheduled' && b.calendar_sync?.status === 'failed' && !past && (
          <div className="iv-note is-bad" data-testid="detail-sync-failed">
            <div>Couldn't add this interview to {b.calendar_sync.provider === 'microsoft' ? 'Outlook' : 'Google Calendar'}. {b.calendar_sync.error} Everyone still got an .ics email invite.</div>
            <div className="iv-row-end" style={{ marginTop: '0.5rem' }}><button type="button" className="iv-btn iv-btn-primary iv-btn-sm" disabled={busy} onClick={retrySync} data-testid="detail-retry-sync-btn"><RefreshCw size={14} /> {busy ? 'Retrying…' : 'Retry sync'}</button></div>
          </div>
        )}
        <div style={{ display: 'grid', gap: '0.35rem' }}>
          <KV k="When">{b.start ? `${fmtSlot(b.start, tz)} (${tz})` : 'Not booked yet'}</KV>
          {b.candidate_timezone && b.start && <KV k="Candidate's time">{fmtSlot(b.start, b.candidate_timezone)} ({b.candidate_timezone})</KV>}
          <KV k="Duration">{b.duration_min} min · {b.panel_size} interviewer{b.panel_size > 1 ? 's' : ''}</KV>
          <KV k="Interviewers">{b.interviewers.map((i) => i.name).join(', ') || '—'}</KV>
          <KV k="Candidate email">{b.candidate_email || 'None on file'}</KV>
          {b.status === 'awaiting_candidate' && <KV k="Link expires">{fmtDate(b.expires_at)}</KV>}
          {b.reminders && Object.keys(b.reminders).length > 0 && <KV k="Reminders">{Object.keys(b.reminders).join(', ')} sent</KV>}
          {b.outcome_note && <KV k="Outcome note">{b.outcome_note}</KV>}
          {b.cancel_reason && <KV k="Cancel reason">{b.cancel_reason}</KV>}
          {b.panel?.total > 0 && <KV k="Scorecards"><span data-testid="detail-scorecards">{b.panel.submitted}/{b.panel.total} submitted{b.panel.verdict ? ` · ${b.panel.verdict}` : ''}</span></KV>}
          <KV k="Where">{b.venue === 'chire' ? 'CHIRE video room' : b.meeting_link ? 'External meeting link' : 'Link to follow'}</KV>
        </div>
        <div className="iv-row">
          {b.room_path && b.venue === 'chire' && b.status !== 'cancelled' && <Link className="iv-btn iv-btn-cta iv-btn-sm" to={b.room_path} data-testid="detail-open-room-btn"><Video size={14} /> Open interview room</Link>}
          {b.room_path && b.venue !== 'chire' && b.status !== 'cancelled' && <Link className="iv-btn iv-btn-ghost iv-btn-sm" to={`${b.room_path}?tab=scorecard`} data-testid="detail-scorecard-btn"><ClipboardCheck size={14} /> Scorecard &amp; brief</Link>}
          {b.meeting_link && b.venue !== 'chire' && <a className="iv-btn iv-btn-primary iv-btn-sm" href={b.meeting_link} target="_blank" rel="noreferrer" data-testid="detail-join-link"><ExternalLink size={14} /> Join meeting</a>}
          {b.schedule_path && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => copyText(absUrl(b.schedule_path), toast)} data-testid="detail-copy-link-btn"><Copy size={14} /> Candidate link</button>}
          {open && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" disabled={busy} onClick={() => run(() => schedulingAPI.resend(b.id), 'Sent again')} data-testid="detail-resend-btn"><Send size={14} /> Resend</button>}
          {b.start && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={ics} data-testid="detail-ics-btn"><Download size={14} /> .ics</button>}
          {open && !past && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => setMode(mode === 'book' ? null : 'book')} data-testid="detail-reschedule-btn"><CalendarClock size={14} /> {b.status === 'scheduled' ? 'Reschedule' : 'Book a time'}</button>}
          {open && <button type="button" className="iv-btn iv-btn-danger iv-btn-sm" onClick={() => setMode(mode === 'cancel' ? null : 'cancel')} data-testid="detail-cancel-btn"><XCircle size={14} /> Cancel</button>}
        </div>
        {mode === 'book' && (
          <div className="iv-card" style={{ marginBottom: 0 }}>
            <DirectSlots poolId={b.pool_id} duration={b.duration_min} panel={b.panel_size} excludeId={b.id} value={slot} onChange={setSlot} who={who} setWho={setWho} />
            <div className="iv-row-end"><button type="button" className="iv-btn iv-btn-primary" disabled={busy || !slot} onClick={() => run(() => schedulingAPI.reschedule(b.id, { slot_start: slot.start, interviewer_ids: who ? [who] : undefined }), b.status === 'scheduled' ? 'Interview rescheduled' : 'Interview booked')} data-testid="detail-reschedule-confirm-btn">Confirm time</button></div>
          </div>
        )}
        {mode === 'cancel' && (
          <div className="iv-card" style={{ marginBottom: 0 }}>
            <textarea className="iv-textarea" rows={2} placeholder="Reason (shared with the candidate)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-cancel-reason" />
            <div className="iv-row-end"><button type="button" className="iv-btn iv-btn-danger" disabled={busy} onClick={() => run(() => schedulingAPI.cancel(b.id, note), 'Interview cancelled')} data-testid="detail-cancel-confirm-btn">Cancel interview</button></div>
          </div>
        )}
        {(started || ['completed', 'no_show'].includes(b.status)) && (
          <div className="iv-card" style={{ marginBottom: 0 }} data-testid="detail-outcome">
            <div className="iv-eyebrow">How did it go?</div>
            <textarea className="iv-textarea" rows={2} placeholder="Short note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-outcome-note" />
            <div className="iv-row-end">
              <button type="button" className="iv-btn iv-btn-ghost" disabled={busy} onClick={() => run(() => schedulingAPI.outcome(b.id, { outcome: 'no_show', note }), 'Marked as no-show')} data-testid="detail-noshow-btn">No-show</button>
              <button type="button" className="iv-btn iv-btn-primary" disabled={busy} onClick={() => run(() => schedulingAPI.outcome(b.id, { outcome: 'completed', note }), 'Marked as completed')} data-testid="detail-completed-btn">Completed</button>
            </div>
          </div>
        )}
        <div>
          <div className="iv-eyebrow" style={{ marginBottom: 4 }}>History</div>
          {b.history.slice(-8).map((h) => <KV key={`${h.at}-${h.action}`} k={h.action.replace(/_/g, ' ')}>{`${fmtDate(h.at)} · ${h.by}${h.start ? ` · ${fmtSlot(h.start, tz)}` : ''}`}</KV>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
